import axios from 'axios'

class LogService {
  constructor() {
    this.consoleLogs = []
    this.networkCalls = []
    this.originalConsole = {}
    this.onLogCountUpdateCallback = null
    this.onNetworkCallUpdateCallback = null
    this.axiosInterceptors = {
      request: null,
      response: null
    }
    
    // Configuration for filtering
    this.config = {
      maxLogLength: 500, // Maximum characters per log entry
      maxStackTraceLines: 5, // Maximum stack trace lines to keep
      excludePatterns: [
        /vue\.runtime\.esm\.js/,
        /node_modules/,
        /webpack/,
        /chunk\.js/,
        /__webpack/
      ],
      summarizeErrors: true, // Whether to summarize long error messages
      maxLogsPerType: 15 // Maximum logs per type (error, warn, log)
    }
  }

  initialize() {
    this.startConsoleLogging()
    this.interceptNetworkCalls()
    this.setupErrorHandlers()
  }

  cleanup() {
    this.stopConsoleLogging()
    this.removeAxiosInterceptors()
    this.removeErrorHandlers()
  }

  startConsoleLogging() {
    // Store original console methods
    this.originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error
    }
    
    // Intercept console methods
    const self = this
    console.log = function(...args) {
      self.originalConsole.log.apply(console, args)
      self.addConsoleLog('log', args)
    }
    
    console.warn = function(...args) {
      self.originalConsole.warn.apply(console, args)
      self.addConsoleLog('warn', args)
    }
    
    console.error = function(...args) {
      self.originalConsole.error.apply(console, args)
      self.addConsoleLog('error', args)
    }
  }

  stopConsoleLogging() {
    if (this.originalConsole.log) {
      console.log = this.originalConsole.log
      console.warn = this.originalConsole.warn
      console.error = this.originalConsole.error
    }
  }

  addConsoleLog(level, args) {
    // Process and clean the log message
    const processedMessage = this.processLogMessage(args)
    
    // Skip if message is too generic or from excluded sources
    if (this.shouldSkipLog(processedMessage, level)) {
      return
    }

    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: processedMessage,
      url: window.location.href,
      // Add context for errors but keep it minimal
      ...(level === 'error' && this.getErrorContext(args))
    }
    
    // Add to appropriate category and maintain limits
    this.addLogWithLimits(logEntry, level)
    this.notifyLogCountUpdate()
  }

  processLogMessage(args) {
    let message = args.map(arg => {
      if (typeof arg === 'object') {
        return this.processObjectArg(arg)
      }
      return String(arg)
    }).join(' ')

    // Truncate if too long
    if (message.length > this.config.maxLogLength) {
      message = message.substring(0, this.config.maxLogLength) + '... [truncated]'
    }

    return message
  }

  processObjectArg(obj) {
    try {
      // Special handling for Error objects
      if (obj instanceof Error) {
        return this.summarizeError(obj)
      }
      
      // Special handling for large objects
      if (this.isLargeObject(obj)) {
        return this.summarizeObject(obj)
      }
      
      return JSON.stringify(obj, this.getCircularReplacer(), 2)
    } catch (e) {
      return `[Object: ${obj.constructor?.name || 'Unknown'}]`
    }
  }

  summarizeError(error) {
    if (!this.config.summarizeErrors) {
      return error.toString()
    }

    let summary = `${error.name}: ${error.message}`
    
    // Add minimal stack trace for debugging
    if (error.stack) {
      const stackLines = error.stack.split('\n')
        .filter(line => !this.isExcludedStackLine(line))
        .slice(0, this.config.maxStackTraceLines)
      
      if (stackLines.length > 1) {
        summary += '\nStack: ' + stackLines.slice(1).join(' -> ')
      }
    }
    
    return summary
  }

  isExcludedStackLine(line) {
    return this.config.excludePatterns.some(pattern => pattern.test(line))
  }

  summarizeObject(obj) {
    // For large objects, just show the type and key properties
    const keys = Object.keys(obj)
    const summary = {
      type: obj.constructor?.name || 'Object',
      keyCount: keys.length,
      keys: keys.slice(0, 5) // Show first 5 keys
    }
    
    // Add important properties if they exist
    if (obj.message) summary.message = String(obj.message).substring(0, 100)
    if (obj.status) summary.status = obj.status
    if (obj.url) summary.url = obj.url
    
    return JSON.stringify(summary)
  }

  isLargeObject(obj) {
    try {
      const str = JSON.stringify(obj)
      return str.length > 1000 // Consider objects > 1KB as large
    } catch (e) {
      return true // If we can't stringify it, consider it large
    }
  }

  getCircularReplacer() {
    const seen = new WeakSet()
    return (key, value) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) {
          return "[Circular]"
        }
        seen.add(value)
      }
      return value
    }
  }

  shouldSkipLog(message, level) {
    // Skip empty or very short messages
    if (!message || message.trim().length < 3) {
      return true
    }
    
    // Skip common Vue.js development messages
    const skipPatterns = [
      /^\[Vue/,
      /vue\.runtime\.esm\.js/,
      /Download the Vue Devtools/,
      /You are running Vue in development mode/,
      /webpack/i,
      /hot reload/i,
      /HMR/i
    ]
    
    return skipPatterns.some(pattern => pattern.test(message))
  }

  getErrorContext(args) {
    // Extract useful error context without being too verbose
    const firstArg = args[0]
    if (firstArg instanceof Error && firstArg.stack) {
      const relevantStackLines = firstArg.stack.split('\n')
        .filter(line => !this.isExcludedStackLine(line))
        .slice(0, 2) // Just first 2 relevant stack lines
      
      return {
        errorType: firstArg.name,
        stackSummary: relevantStackLines.join(' | ')
      }
    }
    return {}
  }

  addLogWithLimits(logEntry, level) {
    // Count logs by level
    const logsOfThisLevel = this.consoleLogs.filter(log => log.level === level)
    
    // Remove oldest logs of this level if we exceed the limit
    if (logsOfThisLevel.length >= this.config.maxLogsPerType) {
      const indexToRemove = this.consoleLogs.findIndex(log => log.level === level)
      if (indexToRemove !== -1) {
        this.consoleLogs.splice(indexToRemove, 1)
      }
    }
    
    this.consoleLogs.push(logEntry)
    
    // Keep total logs under control
    if (this.consoleLogs.length > 50) {
      this.consoleLogs.shift()
    }
  }

  interceptNetworkCalls() {
    const self = this

    // Request interceptor
    this.axiosInterceptors.request = axios.interceptors.request.use(
      config => {
        config.metadata = { startTime: Date.now() }
        return config
      },
      error => {
        this.addNetworkCall({
          url: error.config?.url || 'unknown',
          method: error.config?.method?.toUpperCase() || 'unknown',
          status: 0,
          statusText: 'Request Error',
          requestData: this.sanitizeRequestData(error.config?.data),
          responseData: null,
          timestamp: new Date().toISOString(),
          duration: 0,
          type: 'request_error',
          errorSummary: this.getNetworkErrorSummary(error)
        })
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.axiosInterceptors.response = axios.interceptors.response.use(
      response => {
        const duration = response.config.metadata ? 
          Date.now() - response.config.metadata.startTime : 0

        this.addNetworkCall({
          url: response.config.url,
          method: response.config.method?.toUpperCase() || 'unknown',
          status: response.status,
          statusText: response.statusText,
          requestData: this.sanitizeRequestData(response.config.data),
          responseData: this.sanitizeResponseData(response.data),
          timestamp: new Date().toISOString(),
          duration,
          type: 'success'
        })
        
        return response
      },
      error => {
        const duration = error.config?.metadata ? 
          Date.now() - error.config.metadata.startTime : 0

        this.addNetworkCall({
          url: error.config?.url || 'unknown',
          method: error.config?.method?.toUpperCase() || 'unknown',
          status: error.response?.status || 0,
          statusText: error.response?.statusText || 'Network Error',
          requestData: this.sanitizeRequestData(error.config?.data),
          responseData: this.sanitizeResponseData(error.response?.data),
          timestamp: new Date().toISOString(),
          duration,
          type: 'error',
          errorSummary: this.getNetworkErrorSummary(error)
        })
        
        return Promise.reject(error)
      }
    )
  }

  getNetworkErrorSummary(error) {
    // Create a concise error summary instead of full stack trace
    return {
      message: error.message,
      code: error.code,
      type: error.name,
      // Don't include full stack trace for network errors
      isNetworkError: true
    }
  }

  sanitizeRequestData(data) {
    if (!data) return null
    
    try {
      // Convert to string and limit size
      let dataStr = typeof data === 'string' ? data : JSON.stringify(data)
      
      // Truncate large request data
      if (dataStr.length > 500) {
        dataStr = dataStr.substring(0, 500) + '... [truncated]'
      }
      
      return dataStr
    } catch (e) {
      return '[Request data not serializable]'
    }
  }

  sanitizeResponseData(data) {
    if (!data) return null
    
    try {
      // For successful responses, just capture essential info
      if (typeof data === 'object') {
        const sanitized = {
          type: Array.isArray(data) ? 'array' : 'object',
          size: Array.isArray(data) ? data.length : Object.keys(data).length
        }
        
        // Include small response data, summarize large ones
        const dataStr = JSON.stringify(data)
        if (dataStr.length <= 300) {
          sanitized.data = data
        } else {
          sanitized.summary = 'Large response - ' + dataStr.substring(0, 100) + '...'
        }
        
        return sanitized
      }
      
      // For string responses
      if (typeof data === 'string' && data.length > 300) {
        return data.substring(0, 300) + '... [truncated]'
      }
      
      return data
    } catch (e) {
      return '[Response data not serializable]'
    }
  }

  removeAxiosInterceptors() {
    if (this.axiosInterceptors.request !== null) {
      axios.interceptors.request.eject(this.axiosInterceptors.request)
      this.axiosInterceptors.request = null
    }
    
    if (this.axiosInterceptors.response !== null) {
      axios.interceptors.response.eject(this.axiosInterceptors.response)
      this.axiosInterceptors.response = null
    }
  }

  addNetworkCall(callData) {
    this.networkCalls.push({
      ...callData,
      id: `call_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
    })
    
    // Keep only last 20 network calls (reduced from 25)
    if (this.networkCalls.length > 20) {
      this.networkCalls.shift()
    }

    this.notifyNetworkCallUpdate()
  }

  setupErrorHandlers() {
    this.handleError = this.handleError.bind(this)
    this.handlePromiseRejection = this.handlePromiseRejection.bind(this)
    
    window.addEventListener('error', this.handleError)
    window.addEventListener('unhandledrejection', this.handlePromiseRejection)
  }

  removeErrorHandlers() {
    window.removeEventListener('error', this.handleError)
    window.removeEventListener('unhandledrejection', this.handlePromiseRejection)
  }

  handleError(event) {
    // Only log if it's not from excluded sources
    if (!this.isExcludedStackLine(event.filename || '')) {
      this.addConsoleLog('error', [
        `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`
      ])
    }
  }

  handlePromiseRejection(event) {
    // Summarize promise rejections instead of logging full details
    const reason = typeof event.reason === 'object' ? 
      this.summarizeError(event.reason) : 
      String(event.reason)
    
    this.addConsoleLog('error', [`Unhandled Promise Rejection: ${reason}`])
  }

  onLogCountUpdate(callback) {
    this.onLogCountUpdateCallback = callback
  }

  onNetworkCallUpdate(callback) {
    this.onNetworkCallUpdateCallback = callback
  }

  notifyLogCountUpdate() {
    if (this.onLogCountUpdateCallback) {
      this.onLogCountUpdateCallback(this.consoleLogs.length)
    }
  }

  notifyNetworkCallUpdate() {
    if (this.onNetworkCallUpdateCallback) {
      this.onNetworkCallUpdateCallback(this.networkCalls.length)
    }
  }

  getDebugData() {
    return {
      consoleLogs: this.consoleLogs.slice(-20), // Reduced from 25
      networkCalls: this.networkCalls.slice(-15), // Reduced from 25
      networkCallsSummary: {
        total: this.networkCalls.length,
        successful: this.networkCalls.filter(call => call.type === 'success').length,
        failed: this.networkCalls.filter(call => call.type === 'error').length,
        requestErrors: this.networkCalls.filter(call => call.type === 'request_error').length
      },
      logsSummary: {
        total: this.consoleLogs.length,
        errors: this.consoleLogs.filter(log => log.level === 'error').length,
        warnings: this.consoleLogs.filter(log => log.level === 'warn').length,
        info: this.consoleLogs.filter(log => log.level === 'log').length
      }
    }
  }

  // Utility methods
  getNetworkCallsByType(type) {
    return this.networkCalls.filter(call => call.type === type)
  }

  getRecentNetworkCalls(count = 10) {
    return this.networkCalls.slice(-count)
  }

  getNetworkCallsForUrl(urlPattern) {
    return this.networkCalls.filter(call => 
      call.url.includes(urlPattern) || 
      new RegExp(urlPattern).test(call.url)
    )
  }

  // Configuration methods
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig }
  }

  // Clear all logs (useful for testing)
  clearLogs() {
    this.consoleLogs = []
    this.networkCalls = []
    this.notifyLogCountUpdate()
    this.notifyNetworkCallUpdate()
  }
}

export default new LogService()